'use client'

import React, { useState, useEffect } from 'react'
import { Header } from '@/payload-types'
import { Link } from '@/i18n/routing'
import { XIcon } from 'lucide-react'
import { cn } from '@/utilities/cn'

interface AnnouncementBannerProps {
    announcement: Header['announcement']
}

// Simple hash function to create a unique ID for each announcement message
function hashString(str: string): string {
    let hash = 0
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i)
        hash = (hash << 5) - hash + char
        hash = hash & hash // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36)
}

export function AnnouncementBanner({ announcement }: AnnouncementBannerProps) {
    const [isDismissed, setIsDismissed] = useState(false)
    const [isMounted, setIsMounted] = useState(false)

    // Create a unique storage key based on the announcement text
    // This way, when the message changes, the banner shows again
    const messageHash = announcement?.text ? hashString(announcement.text) : ''
    const storageKey = `announcement-dismissed-${messageHash}`

    useEffect(() => {
        setIsMounted(true)
        // Check if THIS specific announcement was previously dismissed
        const dismissed = localStorage.getItem(storageKey)
        if (dismissed === 'true') {
            setIsDismissed(true)
        }
    }, [storageKey])

    if (!announcement?.enabled || !announcement?.text || isDismissed) {
        return null
    }

    const handleDismiss = () => {
        localStorage.setItem(storageKey, 'true')
        setIsDismissed(true)
    }

    const variant = announcement.variant || 'info'

    const variantStyles = {
        // Phase 7f: the tint stays semantic, the text is the theme foreground —
        // the old text-*-100 was invisible in light mode.
        info: 'bg-blue-500/10 border-blue-500/30 text-foreground',
        warning: 'bg-orange-500/10 border-orange-500/30 text-foreground',
        error: 'bg-red-500/10 border-red-500/30 text-foreground',
    }

    const buttonStyles = {
        info: 'bg-blue-500/20 hover:bg-blue-500/30 border-blue-500/40 text-foreground',
        warning: 'bg-orange-500/20 hover:bg-orange-500/30 border-orange-500/40 text-foreground',
        error: 'bg-red-500/20 hover:bg-red-500/30 border-red-500/40 text-foreground',
    }

    return (
        <div
            className={cn(
                'w-full min-h-12 border-b backdrop-blur-xl shadow-lg flex items-center justify-center px-4 py-2 relative animate-in slide-in-from-top duration-300',
                variantStyles[variant],
            )}
        >
            <div className="container flex items-start justify-between gap-3 sm:gap-4">
                <div className="flex flex-col items-start gap-3 flex-1 min-w-0 sm:flex-row sm:items-center sm:gap-4">
                    <p className="text-sm leading-snug text-left font-medium flex-1 min-w-0 whitespace-normal break-words sm:text-base sm:leading-normal sm:text-center">
                        {announcement.text}
                    </p>

                    {announcement.link?.url && announcement.link?.text && (
                        <Link
                            href={announcement.link.url}
                            className={cn(
                                'inline-flex items-center min-h-11 px-4 py-1.5 rounded-md border text-sm font-medium transition-all duration-200 whitespace-nowrap flex-shrink-0',
                                buttonStyles[variant],
                            )}
                        >
                            {announcement.link.text}
                        </Link>
                    )}
                </div>

                <button
                    onClick={handleDismiss}
                    className="inline-flex items-center justify-center size-11 -my-1.5 hover:bg-foreground/10 rounded-full transition-all duration-200 flex-shrink-0"
                    aria-label="Dismiss announcement"
                >
                    <XIcon className="h-4 w-4" />
                </button>
            </div>
        </div>
    )
}
